import { useContext } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import {
  SkillsClientContext,
  SKILLS_CLIENT_STATES,
} from 'components/shared/Skills/SkillsClientProvider';
import { queryClient } from 'components/shared/ReactQuery/ReactQueryProvider';
import { hasNonNullValues } from 'utils/string_utils';

export function useGetReadinessSkillsForTaggable({
  taggableId,
  taggableType,
  skip = false,
}) {
  const { client, status } = useContext(SkillsClientContext);

  const variables = {
    taggableId,
    taggableType,
    onlyTags: true,
  };

  return useQuery(
    ['get-readiness-skill-tags-for-taggable', taggableId, taggableType],
    async () => {
      return client.request(
        gql`
          query readinessSkillsForTaggable(
            $taggableId: Int!
            $taggableType: String!
          ) {
            readinessSkillsForTaggable(
              taggableId: $taggableId
              taggableType: $taggableType
            ) {
              name
              id
              companyId
              description
              readinessSkillCategory {
                id
                name
              }
            }
          }
        `,
        variables,
      );
    },
    {
      enabled:
        !skip &&
        hasNonNullValues(variables) &&
        status === SKILLS_CLIENT_STATES.SUCCESS,
    },
  );
}

export function useSyncTaggableToReadinessSkills({
  taggableType,
  taggableId,
  readinessSkillIds,
}) {
  const { client } = useContext(SkillsClientContext);

  return useMutation(
    {
      mutationFn: variables =>
        client.request(
          gql`
            mutation SyncTaggableToReadinessSkills(
              $taggableType: String!
              $taggableId: Int!
              $readinessSkillIds: [Int]!
            ) {
              syncTaggableToReadinessSkills(
                taggableType: $taggableType
                taggableId: $taggableId
                readinessSkillIds: $readinessSkillIds
              )
            }
          `,
          variables,
        ),
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'get-readiness-skills-for-taggable',
          taggableType,
          taggableId,
          readinessSkillIds,
        ]);
      },
    },
  );
}

export function useTagAllContentInPath({ readinessSkillId, pathId }) {
  const { client } = useContext(SkillsClientContext);

  return useMutation({
    mutationFn: variables =>
      client.request(
        gql`
          mutation TagAllContentInPath($readinessSkillId: Int!, $pathId: Int!) {
            tagAllContentInPath(
              readinessSkillId: $readinessSkillId
              pathId: $pathId
            )
          }
        `,
        variables,
      ),
  });
}
